import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VAR_LABELS = {
  amount: { hi: 'राशि (₹)', en: 'Amount (₹)', placeholder: '2500' },
  due_date: { hi: 'देय तिथि', en: 'Due date', placeholder: '15 जुलाई' },
  event_name: { hi: 'कार्यक्रम/अवसर', en: 'Event / occasion', placeholder: 'स्वतंत्रता दिवस' },
  event_date: { hi: 'तारीख', en: 'Date', placeholder: '15 अगस्त, सुबह 9 बजे' },
  message: { hi: 'आपका संदेश', en: 'Your message', placeholder: 'यहाँ अपना संदेश लिखें...' },
};

export default function ParentOutreach({ token }) {
  const [templates, setTemplates] = useState([]);
  const [audience, setAudience] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // selected template for modal
  const [language, setLanguage] = useState('hi');
  const [vars, setVars] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, a, h] = await Promise.all([
        fetch(`${API_URL}/api/parent-outreach/templates`, { headers: authHeaders }),
        fetch(`${API_URL}/api/parent-outreach/audience-count`, { headers: authHeaders }),
        fetch(`${API_URL}/api/parent-outreach/history`, { headers: authHeaders }),
      ]);
      const td = await t.json(); if (td.success) setTemplates(td.templates || []);
      const ad = await a.json(); if (ad.success) setAudience(ad.count || 0);
      const hd = await h.json(); if (hd.success) setHistory(hd.campaigns || []);
    } catch (e) {
      console.error('parent outreach load', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openTemplate = (tpl) => {
    setActive(tpl);
    setVars({});
    setTestPhone('');
    setResult(null);
    setLanguage('hi');
  };

  const renderPreview = () => {
    if (!active) return '';
    let body = language === 'en' ? active.body_en : active.body_hi;
    const subs = {
      '{parent_name}': language === 'en' ? 'Parent' : 'अभिभावक',
      '{student_name}': language === 'en' ? 'your child' : 'आपका बच्चा',
      '{school_name}': language === 'en' ? 'Your School' : 'आपका स्कूल',
      '{amount}': vars.amount || '____',
      '{due_date}': vars.due_date || '____',
      '{event_name}': vars.event_name || '____',
      '{event_date}': vars.event_date || '____',
      '{message}': vars.message || '____',
    };
    Object.entries(subs).forEach(([k, v]) => { body = body.split(k).join(v); });
    return body;
  };

  const send = async (isTest) => {
    if (!active) return;
    if (isTest && !testPhone.trim()) { setResult({ ok: false, msg: 'Test नंबर डालें' }); return; }
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/parent-outreach/send`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: active.id,
          language,
          variables: vars,
          test_phone: isTest ? testPhone.trim() : null,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ ok: true, msg: data.message });
        if (!isTest) { loadAll(); }
      } else {
        setResult({ ok: false, msg: data.detail || data.message || 'भेजने में समस्या' });
      }
    } catch (e) {
      setResult({ ok: false, msg: 'Network error' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="parent-outreach-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-outreach-section">
      {/* Header / audience */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold">📲 Parent Outreach</h2>
          <p className="text-sm opacity-90 mt-1">एक क्लिक में अपने सभी parents को WhatsApp message भेजें</p>
        </div>
        <div className="bg-white/15 rounded-xl px-5 py-3 text-center" data-testid="parent-audience-count">
          <p className="text-3xl font-bold">{audience}</p>
          <p className="text-xs opacity-90">Parent contacts</p>
        </div>
      </div>

      {/* Template cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((tpl) => (
          <div key={tpl.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow flex flex-col" data-testid={`template-card-${tpl.id}`}>
            <h3 className="font-bold text-gray-800">{tpl.title_hi}</h3>
            <p className="text-sm text-gray-500 mt-2 flex-1 whitespace-pre-line line-clamp-4">
              {tpl.body_hi
                .replace(/\{parent_name\}/g, 'अभिभावक')
                .replace(/\{school_name\}/g, 'स्कूल')
                .replace(/\{student_name\}/g, 'बच्चा')
                .replace(/\{amount\}/g, '____')
                .replace(/\{due_date\}/g, '____')
                .replace(/\{event_name\}/g, '____')
                .replace(/\{event_date\}/g, '____')
                .replace(/\{message\}/g, '...')}
            </p>
            <button
              onClick={() => openTemplate(tpl)}
              data-testid={`template-send-${tpl.id}`}
              className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-2 rounded-lg transition-colors"
            >
              भेजें →
            </button>
          </div>
        ))}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="parent-outreach-history">
          <h3 className="font-bold text-gray-800 mb-3">📜 हाल के Broadcasts</h3>
          <div className="space-y-2">
            {history.slice(0, 8).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2" data-testid={`history-row-${c.id}`}>
                <span className="text-gray-700">{c.template_title}{c.is_test ? ' (Test)' : ''}</span>
                <span className="text-gray-500">✅ {c.sent}/{c.total_recipients} • {new Date(c.created_at).toLocaleDateString('hi-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Send modal */}
      {active && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="parent-outreach-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">{active.title_hi}</h3>
              <button onClick={() => setActive(null)} data-testid="modal-close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>

            {/* Language toggle */}
            <div className="flex gap-2 mb-4">
              {['hi', 'en'].map((l) => (
                <button key={l} onClick={() => setLanguage(l)} data-testid={`lang-${l}`}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${language === l ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {l === 'hi' ? 'हिंदी' : 'English'}
                </button>
              ))}
            </div>

            {/* Required variable inputs */}
            {(active.required_vars || []).map((v) => (
              <div key={v} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {(VAR_LABELS[v] || {})[language] || v}
                </label>
                {v === 'message' ? (
                  <textarea
                    rows={3}
                    value={vars[v] || ''}
                    onChange={(e) => setVars({ ...vars, [v]: e.target.value })}
                    placeholder={(VAR_LABELS[v] || {}).placeholder}
                    data-testid={`var-input-${v}`}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 outline-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={vars[v] || ''}
                    onChange={(e) => setVars({ ...vars, [v]: e.target.value })}
                    placeholder={(VAR_LABELS[v] || {}).placeholder}
                    data-testid={`var-input-${v}`}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 outline-none"
                  />
                )}
              </div>
            ))}

            {/* Preview */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 my-4">
              <p className="text-xs text-emerald-700 font-medium mb-1">Preview</p>
              <p className="text-sm text-gray-700 whitespace-pre-line" data-testid="message-preview">{renderPreview()}</p>
            </div>

            {/* Test send */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Test नंबर (अपना WhatsApp)"
                data-testid="test-phone-input"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none"
              />
              <button onClick={() => send(true)} disabled={sending} data-testid="send-test-btn"
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50">
                Test भेजें
              </button>
            </div>

            {result && (
              <div data-testid="send-result" className={`text-sm rounded-lg px-3 py-2 mb-3 ${result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                {result.msg}
              </div>
            )}

            <button
              onClick={() => send(false)}
              disabled={sending}
              data-testid="send-broadcast-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl transition-colors disabled:opacity-50"
            >
              {sending ? 'भेजा जा रहा है...' : `सभी ${audience} Parents को भेजें`}
            </button>
            <p className="text-xs text-gray-400 mt-2 text-center">Message आपके स्कूल के WhatsApp connection से भेजा जाएगा।</p>
          </div>
        </div>
      )}
    </div>
  );
}
